import { meetConsentPath } from "@kwp/contracts";
import { useCallback, useEffect, useState } from "react";
import { useStartConsent } from "../../lib/oauth";
import { IconButton } from "../../ui/icons";
import { Dialog } from "../../ui/overlay";
import { useToast } from "../../ui/toast";
import { consentResult, meetErrorText, returnPath, type Account } from "./model";
import { MeetPanel } from "./MeetPanel";

/** Starts Meet consent for an account and comes back to the current page. */
export function useAllowMeet(): (account: Pick<Account, "id" | "workspaceId">) => Promise<void> {
  const start = useStartConsent();
  return useCallback(
    (account) => start(meetConsentPath(account.workspaceId, account.id, returnPath(window.location))),
    [start],
  );
}

/**
 * Shows the result of a Meet consent that returned to this page (`?meet=enabled` or
 * `?meetError=…`) once, then removes the parameters from the address bar.
 */
export function useConsentResultToast(): void {
  const toast = useToast();
  useEffect(() => {
    const result = consentResult(window.location.search);
    if (result === null) return;
    toast(result.kind === "enabled" ? { message: "Meet is allowed. Use the Meet button to start or schedule." } : { message: meetErrorText(result.reason), tone: "error" });
    window.history.replaceState(window.history.state, "", returnPath(window.location));
  }, [toast]);
}

/** The chat header's Meet button and its dialog. */
export function MeetButton({
  workspaceId,
  channelId,
  label,
  memberEmails,
}: {
  workspaceId: string;
  channelId: string;
  label: string;
  memberEmails: readonly string[];
}) {
  const [open, setOpen] = useState(false);
  const allowMeet = useAllowMeet();
  useConsentResultToast();
  return (
    <>
      <IconButton icon="video" label="Meet" title="Start a Google Meet" className="size-8" onClick={() => setOpen(true)} aria-haspopup="dialog" />
      {open ? (
        <Dialog title="Google Meet" description={`The link is posted to ${label}.`} onClose={() => setOpen(false)}>
          <MeetPanel
            workspaceId={workspaceId}
            channelId={channelId}
            defaultTitle={`Meeting in ${label}`}
            memberEmails={memberEmails}
            onAllow={allowMeet}
            onDone={() => setOpen(false)}
          />
        </Dialog>
      ) : null}
    </>
  );
}
